/**
 * MainTileSection — Plan 05-12 Task 2.
 *
 * Renders a labeled tile section with a CSS grid layout, dispatching each
 * tile ID to the correct tile-family component via MAIN_TILE_MAPPING.
 *
 * Per UI-SPEC §"Tile grid":
 *   - Section header: h2 uppercase semibold ink-500 (section-label class)
 *   - Grid: grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4
 *   - MainTableTile default col-span-2 on lg
 *   - MainBucketedTile can span col-span-2 on lg when note demands wider
 */

import { getMetricDefinition } from '../../lib/definitions.js';
import { MAIN_TILE_MAPPING } from '../../lib/main-tile-mapping.js';

import { MainBarTile } from './MainBarTile.js';
import { MainBucketedTile } from './MainBucketedTile.js';
import { MainOppsCreatedTile } from './MainOppsCreatedTile.js';
import { MainPacingTile } from './MainPacingTile.js';
import { MainPlaceholderTile } from './MainPlaceholderTile.js';
import { MainTreemapTile } from './MainTreemapTile.js';
import { MainTrendTile } from './MainTrendTile.js';

import type { MainScorecardResponse } from '@saledash/shared';

interface MainTileSectionProps {
  sectionKey: string;
  label: string;
  tileIds: string[];
  scorecardData: MainScorecardResponse | undefined;
  isLoading?: boolean;
}

const noop = () => undefined;

/**
 * Sections that opt into the fixed 12-col span grid (quick-260626-g28 Task 3 +
 * quick-260626-j5u Task 6). Each tile's `span` packs rows exactly; below `lg`
 * tiles collapse to full width. Other sections keep the auto-fit behavior.
 */
const SPAN_GRID_SECTIONS = new Set<string>(['headline', 'activity', 'efficiency']);

/**
 * 12-col span → Tailwind class (quick-260626-g28 Task 3). Static class strings
 * (Tailwind can't see interpolated names). Each tile is full width below `lg`
 * and occupies `span` of 12 columns at `lg+`. Used only by the ACTIVITY section
 * which opts into a fixed 12-col grid; other sections keep auto-fit.
 */
const SPAN_CLASS: Record<number, string> = {
  1: 'col-span-12 lg:col-span-1',
  2: 'col-span-12 lg:col-span-2',
  3: 'col-span-12 lg:col-span-3',
  4: 'col-span-12 lg:col-span-4',
  5: 'col-span-12 lg:col-span-5',
  6: 'col-span-12 lg:col-span-6',
  7: 'col-span-12 lg:col-span-7',
  8: 'col-span-12 lg:col-span-8',
  9: 'col-span-12 lg:col-span-9',
  10: 'col-span-12 lg:col-span-10',
  11: 'col-span-12 lg:col-span-11',
  12: 'col-span-12',
};

export function MainTileSection({
  sectionKey,
  label,
  tileIds,
  scorecardData,
  isLoading = false,
}: MainTileSectionProps): React.JSX.Element {
  return (
    <section aria-labelledby={`section-${sectionKey}`}>
      {/* Section header per mock .sec-head: label + a gradient hairline rule
          that trails to the right edge (ink-200 → transparent). */}
      <div className="mb-3 flex items-center gap-3">
        <h2 id={`section-${sectionKey}`} className="section-label shrink-0">
          {label}
        </h2>
        <span
          aria-hidden
          className="h-px flex-1 bg-gradient-to-r from-ink-200 to-transparent"
        />
      </div>
      {/* The ACTIVITY + PIPELINE EFFICIENCY sections opt into a fixed 12-col grid
          for deterministic rows (quick-260626-g28 Task 3 + quick-260626-j5u
          Task 6): per-tile `span` packs rows exactly (3×4, 2×6, full 12). Every
          other section keeps the auto-fit behavior: equal-height rows
          (items-stretch), KPI tiles auto-fitting into as many ~260px columns as
          fit so a 3/5/6/7-KPI section never leaves a dead trailing column; wide
          tiles span the full row. */}
      {SPAN_GRID_SECTIONS.has(sectionKey) ? (
        <div className="grid grid-cols-12 items-stretch gap-3">
          {tileIds.map((metricId) => {
            const mapping = MAIN_TILE_MAPPING[metricId];
            if (!mapping) return null;
            // Fallback: chart/table tiles default to full width; KPIs to half.
            const fallbackSpan =
              mapping.component === 'bucketed' ||
              mapping.component === 'table' ||
              mapping.component === 'treemap'
                ? 12
                : 6;
            const span = mapping.span ?? fallbackSpan;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- SPAN_CLASS[12] is a constant key of the map
            const spanClass = SPAN_CLASS[span] ?? SPAN_CLASS[12]!;
            return (
              <div key={metricId} className={spanClass} data-testid={`tile-wrapper-${metricId}`}>
                {renderTile(metricId, mapping.component, mapping.chartType, scorecardData, isLoading)}
              </div>
            );
          })}
        </div>
      ) : (
        <div
          className="grid items-stretch gap-3"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          }}
        >
          {tileIds.map((metricId) => {
            const mapping = MAIN_TILE_MAPPING[metricId];
            if (!mapping) return null;

            // Full-row width for chart + table tiles. Marketing charts group by
            // Industry / Source / Reason with 15+ categories — even half-row
            // clipped most labels. KPI (trend + placeholder) tiles ride the
            // auto-fit columns above so rows stay full with no dead column.
            const wideComponents = new Set(['table', 'bucketed', 'treemap']);
            // The pacing tile (MAIN-17) is a FEATURED hero — spans 2 columns in
            // the headline band per the mock (.card.feature), not the full row.
            const colSpanClass =
              mapping.component === 'pacing'
                ? 'col-span-2'
                : wideComponents.has(mapping.component)
                  ? 'col-[1/-1]'
                  : '';

            return (
              <div key={metricId} className={colSpanClass} data-testid={`tile-wrapper-${metricId}`}>
                {renderTile(metricId, mapping.component, mapping.chartType, scorecardData, isLoading)}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * Dispatch a metric ID to the correct tile-family component.
 */
function renderTile(
  metricId: string,
  component: string,
  chartType: string | undefined,
  data: MainScorecardResponse | undefined,
  isLoading: boolean,
): React.ReactNode {
  // MAIN-03 Opportunities Created (2026-07-22, John): render the EXACT CRO
  // "Opps Created (trailing 90 days)" tile instead of the legacy quarterly
  // stacked bar. MainOppsCreatedTile owns its own /api/main/opps-created query
  // (the hook can't live here — renderTile is a plain function, not a component).
  // The MAIN-03 mapping entry is retained (so the buildable-mapping + ACTIVITY
  // layout contract tests stay green) but its grouped payload is now unused.
  if (metricId === 'MAIN-03') {
    return <MainOppsCreatedTile />;
  }

  // Get data slices from scorecard
  const metricData = data?.metrics[metricId];
  const groupedData = data?.grouped[metricId];
  const snapData = data?.snap[metricId];

  // Resolve human-readable name + formula from definitions.json. Tiles previously
  // rendered with `title={metricId}` (e.g., "MAIN-01"); the definition lookup
  // upgrades that to the proper metric name and adds the "?" tooltip formula
  // to match the CRO tile UX. Failures here are non-fatal: any metric not yet
  // in definitions.json falls back to the raw ID.
  let title = metricId;
  let tooltipFormula: string | undefined;
  try {
    const def = getMetricDefinition(metricId);
    title = def.name;
    tooltipFormula = def.formula_plain_english;
  } catch {
    // metricId not in definitions.json — leave title as raw ID, no tooltip.
  }

  switch (component) {
    case 'trend':
      return (
        <MainTrendTile
          metricId={metricId}
          title={title}
          tooltipFormula={tooltipFormula}
          data={metricData}
          snapshots={snapData?.snapshots ?? []}
          isLoading={isLoading}
          isError={false}
          error={null}
          refetch={noop}
        />
      );

    case 'bucketed':
      return (
        <MainBucketedTile
          metricId={metricId}
          title={title}
          tooltipFormula={tooltipFormula}
          chartType={(chartType as 'stackedBar' | 'area' | 'lineByQuarter' | 'dailyBar' | 'weeklyBar' | undefined) ?? 'stackedBar'}
          data={groupedData}
          // quick-260701-dea FIX B: thread the snapshot series so snapshot-backed
          // bucketed tiles render a WoW pill (mirrors the `trend` case above).
          // Tiles with no snap series get `[]` → no chip.
          snapshots={snapData?.snapshots ?? []}
          isLoading={isLoading}
          isError={false}
          error={null}
          refetch={noop}
        />
      );

    case 'pacing': {
      // The pacing tile reads BOTH metrics[MAIN-17] (headline + target) and the
      // companion grouped series grouped[MAIN-48] (quick-260626-cot Task 3.2).
      const pacingSeries = data?.grouped['MAIN-48'];
      return (
        <MainPacingTile
          metricId={metricId}
          title={title}
          tooltipFormula={tooltipFormula}
          metric={metricData}
          series={pacingSeries}
          isLoading={isLoading}
          isError={false}
          error={null}
          refetch={noop}
        />
      );
    }

    case 'treemap':
      return (
        <MainTreemapTile
          metricId={metricId}
          title={title}
          tooltipFormula={tooltipFormula}
          data={groupedData}
          isLoading={isLoading}
          isError={false}
          error={null}
          refetch={noop}
        />
      );

    case 'bar': {
      const mapping = MAIN_TILE_MAPPING[metricId];
      return (
        <MainBarTile
          metricId={metricId}
          title={title}
          tooltipFormula={tooltipFormula}
          variant={mapping?.barVariant ?? 'horizontalBar'}
          valueUnit={mapping?.barValueUnit ?? 'number'}
          data={groupedData}
          isLoading={isLoading}
          isError={false}
          error={null}
          refetch={noop}
        />
      );
    }

    case 'placeholder': {
      const mapping = MAIN_TILE_MAPPING[metricId];
      return (
        <MainPlaceholderTile
          title={title}
          primary={mapping?.xlsxNotes ?? 'Deferred — Phase 2'}
          secondary="This tile will be available in a future release."
        />
      );
    }

    default:
      return null;
  }
}
